import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "utils";

import { CROP_TYPE } from "models";

import { Flex, Icon, TooltipWrapper } from "components";

export type CropCardProps = {
  cropType: CROP_TYPE;
  cropName: string;
  cropId: string;
  isActive?: boolean;
  hasResidue?: boolean;
  dataTestId?: string;
  mRef?: React.RefObject<HTMLDivElement>;
};

export const CropCardHeader = ({ label, name, dataTestId }: { label: string; name: string; dataTestId: string }) => {
  const nameContainerRef = useRef<HTMLParagraphElement | null>(null);
  const nameRef = useRef<HTMLParagraphElement | null>(null);
  const [shouldShowTooltip, setShouldShowTooltip] = useState<boolean>(false);

  useEffect(() => {
    if (!nameContainerRef.current || !nameRef.current) return;

    if (nameContainerRef.current.offsetWidth < nameRef.current.offsetWidth) {
      setShouldShowTooltip(true);
      return;
    }
    setShouldShowTooltip(false);
  }, [name]);

  return (
    <Flex vertical={true} align="start">
      <p data-testid={`${dataTestId}-header-label`} className="label">
        {label}
      </p>
      <p ref={nameContainerRef} data-testid={`${dataTestId}-header-name`} className="name-container">
        <TooltipWrapper tooltip={shouldShowTooltip ? { title: name, placement: "top" } : {}}>
          <span ref={nameRef} className="name">
            {name}
          </span>
        </TooltipWrapper>
      </p>
    </Flex>
  );
};

export const CropCard = ({ cropType, cropName, isActive, hasResidue, dataTestId, mRef }: CropCardProps) => {
  const { t } = useTranslation("cropExplorer");
  const [currentRef, setCurrentRef] = useState<React.RefObject<HTMLDivElement>>();
  useEffect(() => {
    if (mRef) setCurrentRef(mRef);
  }, [mRef]);

  return (
    <Flex ref={currentRef} data-testid={`crop-card-${dataTestId}`} className={`crop-card layout ${isActive && "active"}`} vertical={true}>
      <CropCardHeader dataTestId={`crop-card-${dataTestId}`} label={t(`cropCard.${cropType.toLowerCase()}`)} name={cropName} />
      {hasResidue && (
        <Flex className="margin-top-50">
          <TooltipWrapper tooltip={{ placement: "bottom", title: t("cropCard.residueLeftOnField") }} className="residue-icon-container">
            <Icon dataTestId={`crop-card-${dataTestId}-icon`} className="residue-icon" iconName="tabler/growth" />
          </TooltipWrapper>
        </Flex>
      )}
    </Flex>
  );
};
